// Task #197 — pure helpers for the redesigned "Team performance —
// Assessments" card. Keeping these out of the React tree means the
// thresholds and sort logic can be unit-tested without mounting
// anything, and tuned later without touching the rendering code.

import type { AggregateRow, TeamTriageRow } from "@/lib/api";

export type SeverityTier = "red" | "amber" | "green" | "neutral";

export type TriageSort = "triage" | "name" | "volume";

// Thresholds picked so today's "decent" teams land in green and a
// team that drops a measure by more than 10pp pops to amber even if
// its absolute rate looks fine. Centralised here so a future tuning
// pass is a one-line change rather than a hunt through the JSX.
export const SEVERITY_THRESHOLDS = {
  red: 0.5, // overall < 50% ⇒ red
  amber: 0.7, // overall < 70% ⇒ amber
  // Negative delta vs. prior that bumps a team one tier worse.
  worseningDelta: 0.1, // 10pp
} as const;

// Task #215 — minimum graded-call count before a team's headline
// percentages are treated as trustworthy. Rows with 1..(N-1) calls
// render in a muted "Low sample" state with a neutral severity dot
// so a single call can't flip a team into the red on the triage
// list. Kept as a single constant so a future tuning pass is a
// one-line change and tooltip copy can reference the number.
export const LOW_SAMPLE_THRESHOLD = 5;

// True when a team has *some* graded calls but fewer than the
// trustworthiness threshold. Zero-call teams keep their existing
// "No graded calls" treatment and are not considered low-sample.
export function isLowSample(callCount: number): boolean {
  return callCount > 0 && callCount < LOW_SAMPLE_THRESHOLD;
}

// Call-weighted overall detection rate across every (measure, label)
// row. Matches what ``TeamHeadlineStats`` shows so the triage list
// and the expanded card stay numerically consistent.
export function overallDetectionRate(rows: AggregateRow[] | undefined): number {
  if (!rows || rows.length === 0) return 0;
  const weight = rows.reduce((sum, r) => sum + r.calls, 0);
  if (weight <= 0) return 0;
  const weighted = rows.reduce(
    (sum, r) => sum + r.detection_rate * r.calls,
    0,
  );
  return weighted / weight;
}

// Returns the row with the largest negative delta vs. prior (the
// "driving number" shown in the headline). When there's no usable
// prior, falls back to the lowest absolute detection rate so the
// row still surfaces *something* actionable. ``null`` only when the
// team has no detection rows at all.
export function worstMeasure(
  rows: AggregateRow[] | undefined,
  prior: AggregateRow[] | undefined,
): { row: AggregateRow; delta: number | null } | null {
  if (!rows || rows.length === 0) return null;
  const priorMap = new Map<string, number>();
  for (const r of prior ?? []) {
    priorMap.set(`${r.measure_id}::${r.label_name}`, r.detection_rate);
  }
  let pick: AggregateRow | null = null;
  let pickDelta: number | null = null;
  // Two-pass: first try the largest negative delta vs. prior; if
  // no prior was provided we fall through to lowest rate.
  if (prior && prior.length > 0) {
    let worstDelta = Infinity;
    for (const r of rows) {
      const p = priorMap.get(`${r.measure_id}::${r.label_name}`);
      if (p === undefined) continue;
      const d = r.detection_rate - p;
      if (d < worstDelta) {
        worstDelta = d;
        pick = r;
        pickDelta = d;
      }
    }
  }
  if (!pick) {
    let lowest = Infinity;
    for (const r of rows) {
      if (r.detection_rate < lowest) {
        lowest = r.detection_rate;
        pick = r;
      }
    }
    pickDelta = null;
  }
  return pick ? { row: pick, delta: pickDelta } : null;
}

// Severity tier for one team. ``teamAverage`` lets us soften / harden
// the call: a team well below the floor *and* below the cross-team
// average is unambiguously red; a team that's below the floor but
// at or above the cohort average is amber (the whole org has the
// problem, not just this team).
export function severityTier(
  team: TeamTriageRow,
  teamAverage: number,
): SeverityTier {
  if (team.call_count === 0) return "neutral";
  // Low-sample teams can't be tier-ranked reliably — a single call
  // would flip the colour — so render the dot as neutral and let
  // the row's "Low sample" chip explain why.
  if (isLowSample(team.call_count)) return "neutral";
  const overall = overallDetectionRate(team.per_label);
  const worst = worstMeasure(team.per_label, team.prior_per_label);
  const delta = worst?.delta ?? null;
  let tier: SeverityTier;
  if (overall < SEVERITY_THRESHOLDS.red) tier = "red";
  else if (overall < SEVERITY_THRESHOLDS.amber) tier = "amber";
  else tier = "green";
  // A steep negative delta bumps a tier worse even if the absolute
  // value still looks acceptable — a team that just dropped from
  // 85% to 72% is the one the manager actually needs to look at.
  if (delta !== null && delta <= -SEVERITY_THRESHOLDS.worseningDelta) {
    if (tier === "green") tier = "amber";
    else if (tier === "amber") tier = "red";
  }
  // Soften red ⇒ amber when this team is at or above the cohort
  // average — the whole org has the problem.
  if (tier === "red" && overall >= teamAverage && teamAverage > 0) {
    tier = "amber";
  }
  return tier;
}

// Triage sort: worst team first. We rank by (1) lowest overall rate
// and (2) most-negative delta vs. prior — the row that hurts the
// most floats up. Stable secondary tiebreak on team_name so the
// list is deterministic across re-renders.
export function triageSort(rows: TeamTriageRow[]): TeamTriageRow[] {
  return rows
    .slice()
    .sort((a, b) => {
      // Teams with zero calls go to the bottom — there's nothing to
      // triage about a team that hasn't been graded yet.
      const aHas = a.call_count > 0;
      const bHas = b.call_count > 0;
      if (aHas !== bHas) return aHas ? -1 : 1;
      // Low-sample teams sit *below* adequately-sampled rows
      // (regardless of their raw percentages) so the manager's
      // eye lands on rows whose tier is actually trustworthy. The
      // existing tie-breakers still apply within each group.
      const aLow = isLowSample(a.call_count);
      const bLow = isLowSample(b.call_count);
      if (aLow !== bLow) return aLow ? 1 : -1;
      const aOverall = overallDetectionRate(a.per_label);
      const bOverall = overallDetectionRate(b.per_label);
      // Lower overall rate ⇒ worse ⇒ earlier in the list.
      if (Math.abs(aOverall - bOverall) > 1e-9) return aOverall - bOverall;
      const aDelta =
        worstMeasure(a.per_label, a.prior_per_label)?.delta ?? 0;
      const bDelta =
        worstMeasure(b.per_label, b.prior_per_label)?.delta ?? 0;
      // More-negative delta ⇒ worse ⇒ earlier.
      if (Math.abs(aDelta - bDelta) > 1e-9) return aDelta - bDelta;
      return a.team_name.localeCompare(b.team_name);
    });
}

// Cohort average detection rate across the supplied teams. Used as
// the "team average" comparison overlay on every measure bar and as
// a softening signal in ``severityTier``.
export function cohortAverageRate(rows: TeamTriageRow[]): number {
  // Pool every per_label across every team, then take the
  // call-weighted overall. Mirrors how the headline stat is
  // computed on each card so the two surfaces always agree.
  const pooled: AggregateRow[] = [];
  for (const t of rows) pooled.push(...t.per_label);
  return overallDetectionRate(pooled);
}

// Per-(measure, label) team average across the cohort — used to
// render the small "team avg" marker on each MeasureBarChart bar.
// Returns a map keyed by ``"${measure_id}::${label_name}"``.
export function cohortAveragePerLabel(
  rows: TeamTriageRow[],
): Map<string, number> {
  const sums = new Map<string, { weighted: number; weight: number }>();
  for (const t of rows) {
    for (const r of t.per_label) {
      const key = `${r.measure_id}::${r.label_name}`;
      const cur = sums.get(key) ?? { weighted: 0, weight: 0 };
      cur.weighted += r.detection_rate * r.calls;
      cur.weight += r.calls;
      sums.set(key, cur);
    }
  }
  const out = new Map<string, number>();
  for (const [k, v] of sums) {
    out.set(k, v.weight > 0 ? v.weighted / v.weight : 0);
  }
  return out;
}

// Pretty-print a percentage point delta for the headline UI ("↓12pp"
// / "↑3pp" / "" when not comparable). Centralised so the triage row
// and the agent drill render the same wording.
export function formatDeltaPp(delta: number | null): string {
  if (delta === null || Number.isNaN(delta)) return "";
  const pp = Math.round(delta * 100);
  if (pp === 0) return "0pp";
  return pp > 0 ? `↑${pp}pp` : `↓${Math.abs(pp)}pp`;
}
